import { useCallback, useEffect, useMemo, useState } from 'react'
import { CeleryTaskStatusResponse } from '@/api/generalApi'
import { usePollTaskStatus } from '../queries/generalQueries'

type UseWaitForCeleryTaskResultsAsyncReturn<T> = {
  isPending: boolean
  waitForCeleryTaskResultsAsync: (
    taskId: string
  ) => Promise<CeleryTaskStatusResponse<T>>
}

export const useWaitForCeleryTaskResultsAsync = <
  T = unknown,
>(): UseWaitForCeleryTaskResultsAsyncReturn<T> => {
  const [currentTaskId, setCurrentTaskId] = useState<string | null>(null)

  const [pendingPromise, setPendingPromise] = useState<{
    resolve: (value: CeleryTaskStatusResponse<T>) => void
    reject: (reason?: unknown) => void
  } | null>(null)

  const {
    data: taskData,
    isLoading,
    error
  } = usePollTaskStatus(currentTaskId || '', !!currentTaskId)

  useEffect(() => {
    if (!taskData || !pendingPromise) {
      return
    }

    switch (taskData.task_status) {
      case 'SUCCESS':
        pendingPromise.resolve(taskData as CeleryTaskStatusResponse<T>)
        setCurrentTaskId(null)
        setPendingPromise(null)
        break
      case 'FAILURE':
        pendingPromise.reject(taskData)
        setCurrentTaskId(null)
        setPendingPromise(null)
        break
    }
  }, [taskData, pendingPromise])

  useEffect(() => {
    if (error && pendingPromise) {
      pendingPromise.reject()
      setCurrentTaskId(null)
      setPendingPromise(null)
    }
  }, [error, pendingPromise])

  const waitForCeleryTaskResultsAsync = useCallback(
    async (taskId: string): Promise<CeleryTaskStatusResponse<T>> => {
      return new Promise((resolve, reject) => {
        setCurrentTaskId(taskId)
        setPendingPromise({ resolve, reject })
      })
    },
    []
  )

  return useMemo(
    () => ({
      isPending: isLoading || taskData?.task_status === 'PENDING',
      waitForCeleryTaskResultsAsync
    }),
    [isLoading, taskData?.task_status, waitForCeleryTaskResultsAsync]
  )
}
